import {useMemo, type FC, type HTMLAttributes} from 'react'
import {clsx} from 'clsx'
import {Box, merge} from '@primer/react'
import {useSortable} from '@dnd-kit/sortable'
import {SortableListTrigger} from './SortableListTrigger'
import {useDragAndDrop} from '../hooks/use-drag-and-drop'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {SortableItemContext} from '../context/SortableItemContext'
import type {DragAndDropItem} from '../utils/types'
import {testIdProps} from '@github-ui/test-id-props'

const containerSashStyles: BetterSystemStyleObject = {
  position: 'relative',
  '&::after,  &::before': {
    content: '""',
    display: 'none',
    position: 'absolute',
    bg: 'accent.emphasis',
    border: 0,
    zIndex: 1,
  },

  '&.vertical::after,  &.vertical::before': {
    left: 0,
    right: 0,
    height: '2px',
  },
  '&.horizontal::after,  &.horizontal::before': {
    top: 0,
    bottom: 0,
    width: '2px',
  },

  'body.is-dragging &.show-sash-over.horizontal': {
    '&:before': {
      display: 'block',
      left: 0,
      transform: 'translateX(-50%)',
    },
    '&:after': {
      display: 'block',
      right: 0,
      transform: 'translateX(50%)',
    },
  },
  'body.is-dragging &.show-sash-over.vertical': {
    '&:before': {
      display: 'block',
      top: 0,
      transform: 'translateY(-50%)',
    },
    '&:after': {
      display: 'block',
      bottom: 0,
      transform: 'translateY(50%)',
    },
  },
}

export interface SortableListItemProps extends Omit<HTMLAttributes<HTMLLIElement>, 'id' | 'title'> {
  /**
   * The index of the list item
   */
  index: number
  /**
   * Sx to apply to the container
   */
  containerSx?: BetterSystemStyleObject
  /**
   * Whether or not to hide the trigger, only use when defining a custom trigger.
   * Note: defining a custom trigger is very risky please reference the accessibility grabberIcon [docs](https://github.com/github/accessibility/blob/aa85cdd00de58509aee9eaba18718d2bd727e777/docs/coaching-recommendations/drag-and-drop/technical-guidance/grabber-iconButton.md) to ensure your trigger will be accessible.
   */
  UNSAFE_hideSortableItemTrigger?: boolean
}

/**
 *
 * A sortable list item. This component is responsible for managing the drag trigger and preview.
 *
 * @param props SortableListItemProps
 */
export const SortableListItem: FC<SortableListItemProps & DragAndDropItem> = ({
  children,
  id,
  index,
  title,
  containerSx,
  style,
  UNSAFE_hideSortableItemTrigger,
  ...props
}) => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const {setNodeRef, isDragging, transform, transition} = useSortable({
    id,
    data: {metadata: {id}},
  })

  const {isDropTarget, direction} = useDragAndDrop()

  const isOver = isDropTarget(id)

  const containerClassName = clsx(props.className, {
    ['color-bg-subtle']: isOver && isDragging,
    ['show-sash-over']: isOver,
    ['horizontal']: direction === 'horizontal',
    ['vertical']: direction === 'vertical',
  })

  const containerStyles: BetterSystemStyleObject = containerSx
    ? merge<BetterSystemStyleObject>(containerSashStyles, containerSx)
    : containerSashStyles

  const draggingSx: BetterSystemStyleObject = isDragging
    ? {
        zIndex: 2,
        outline: '1px solid',
        outlineColor: 'border.muted',
        borderRadius: 2,
        backgroundColor: 'canvas.default',
        boxShadow: 'shadow.large',
      }
    : {}

  const sx: BetterSystemStyleObject = merge<BetterSystemStyleObject>(
    {
      position: 'relative',
      transform: transform
        ? `
        translateX(${Math.round(transform.x + (isDragging ? 8 : 0))}px)
        translateY(${Math.round(transform.y - (isDragging ? 8 : 0))}px)
        scaleX(${transform.scaleX})
        scaleY(${transform.scaleY})
      `
        : 'none',
      transition: prefersReducedMotion ? 'none' : transition,
      overflow: 'hidden',
    },
    draggingSx,
  )

  return (
    <SortableItemContext.Provider value={useMemo(() => ({index, title, id}), [index, id, title])}>
      <Box
        as="li"
        {...props}
        {...testIdProps('sortable-item')}
        className={containerClassName}
        sx={containerStyles}
        ref={setNodeRef}
      >
        <Box style={style} sx={sx}>
          {!UNSAFE_hideSortableItemTrigger && <SortableListTrigger />}
          {children}
        </Box>
      </Box>
    </SortableItemContext.Provider>
  )
}

try{ SortableListItem.displayName ||= 'SortableListItem' } catch {}